<template>
  <div class="landing-page">
    <!-- Header -->
    <header class="header">
      <div class="header-container">
        <nav class="nav">
          <!-- Logo -->
          <div class="logo">
            <a href="https://intelite.com.br/" target="_blank" rel="noopener noreferrer">
              <img 
                src="/landing/logos/logo-intelite.png" 
                alt="Intelite Tecnologia" 
                class="logo-image"
              />
            </a>
          </div>

          <!-- Menu Desktop -->
          <div class="nav-desktop">
            <button
              v-for="item in menuItems"
              :key="item.id"
              @click="scrollToSection(item.id)"
              class="nav-button"
              :class="{ 'active': activeSection === item.id }"
            >
              <span 
                class="nav-underline"
                :class="{ 'active': activeSection === item.id }"
              ></span>
              {{ item.label }}
            </button>
          </div>

          <!-- Botão Login Desktop -->
          <div class="login-desktop">
            <button
              @click="goToLogin"
              class="login-button"
            >
              <svg class="login-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
              </svg>
              LOGIN
            </button>
          </div>

          <!-- Menu Mobile Button -->
          <button
            @click="toggleMenu"
            class="menu-toggle"
          >
            <svg
              class="menu-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                v-if="!isMenuOpen"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M4 6h16M4 12h16M4 18h16"
              />
              <path
                v-else
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </nav>
      </div>

      <!-- Overlay escuro -->
      <div v-if="isMenuOpen" class="overlay" @click="toggleMenu"></div>

      <!-- Menu Mobile -->
      <div
        class="nav-mobile"
        :class="{ 'open': isMenuOpen }"
      >
        <div class="nav-mobile-content">
          <button
            v-for="item in menuItems"
            :key="item.id"
            @click="scrollToSection(item.id)"
            class="nav-button-mobile"
            :class="{ 'active': activeSection === item.id }"
          >
            {{ item.label }}
          </button>
          
          <!-- Botão Login Mobile -->
          <button
            @click="goToLogin"
            class="login-button-mobile"
          >
            <svg class="login-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
            </svg>
            LOGIN
          </button>
        </div>
      </div>
    </header>

    <!-- Conteúdo principal -->
    <main class="main">
      <!-- Seção Home -->
      <section id="home" class="section home-section">
        <div class="home-background-container">
          <img src="/landing/logos/BG.png" alt="Background" class="home-background">
          <div class="home-content">
            <div class="home-left">
              <img src="/landing/pessoas/person.png" alt="Pessoa" class="home-person animate-slide-left">
            </div>
            <div class="home-center">
            <div class="home-title">
              <div class="text-line text-white">Pedidos,</div>
              <div class="text-line">pagamentos</div>
              <div class="text-line text-white">e recargas de</div>
              <div class="text-line">forma simples</div>
              <div class="text-line">e eficaz.</div>
            </div>
            </div>
            <div class="home-right"></div>
          </div>
          <img src="/landing/images/cards.png" alt="Bolsa AiConsumer" class="home-cards animate-bounce-in">
        </div>
      </section>

      <!-- Banner 2 -->
      <section class="banner2-section">
        <div class="banner2-content">
          <img src="/landing/logos/banner2.png" alt="Banner 2" class="banner2-image">
        </div>
      </section>

      <!-- Seção Sobre -->
      <section id="sobre" class="section sobre-section">
        <div class="section-content animate-fade-in">
          <h1 class="animate-slide-up">Sobre o AiConsumer Escolas</h1>
          <p class="animate-slide-up-delay">O Aiconsumer foi desenvolvido para simplificar o processo de consumo em shows, festas e outras ocasiões, eliminando filas, notas manuscritas e pagamentos em dinheiro físico. Nossa tecnologia visa aprimorar a experiência dos participantes e organizadores, além de otimizar a gestão financeira e operacional. Com esta solução, você poderá gerenciar o consumo de maneira eficiente, proporcionando um atendimento mais ágil e uma melhor experiência para todos os envolvidos.</p>
        </div>
      </section>

      <!-- Grid de vantagens (check + texto) - abaixo do Sobre, acima da seção Vantagens -->
      <section class="vantagens-grid-section">
        <div class="vantagens-grid-container animate-fade-in">
          <div class="vantagens-grid">
            <div
              v-for="(texto, index) in vantagensGridTextos"
              :key="index"
              class="vantagens-grid-item"
              :class="`animate-bounce-in-delay-${index + 1}`"
            >
              <img
                src="/landing/images/vantagens-grid/check.png"
                :alt="texto"
                class="vantagens-grid-check"
                loading="lazy"
              />
              <p class="vantagens-grid-texto">{{ texto }}</p>
            </div>
          </div>
        </div>
      </section>

      <!-- Título da Seção Vantagens -->
      <section id="vantagens" class="vantagens-title-section">
        <div class="vantagens-title-container animate-fade-in">
          <img src="/landing/images/vantagens_consumer.png" alt="Vantagens AiConsumer" class="vantagens-title-image animate-scale-in">
        </div>
      </section>

      <!-- Seção Vantagens -->
      <section class="vantagens-section">
        <div class="vantagens-container animate-fade-in">
          <div class="vantagens-list">
            <div class="vantagem-item animate-slide-left">
              <span class="vantagem-checkbox" aria-hidden="true"></span>
              <p class="vantagem-text">Venda assistida;</p>
            </div>
            <div class="vantagem-item animate-slide-left-delay-1">
              <span class="vantagem-checkbox" amoria-hidden="true"></span>
              <p class="vantagem-text">Controle de vendas online;</p>
            </div>
            <div class="vantagem-item animate-slide-left-delay-2">
              <span class="vantagem-checkbox" aria-hidden="true"></span>
              <p class="vantagem-text">Comunicação com o público através de whatsapp;</p>
            </div>
            <div class="vantagem-item animate-slide-left-delay-3">
              <span class="vantagem-checkbox" aria-hidden="true"></span>
              <p class="vantagem-text">Histórico de consumo via whatsapp;</p>
            </div>
            <div class="vantagem-item animate-slide-left-delay-4">
              <span class="vantagem-checkbox" aria-hidden="true"></span>
              <p class="vantagem-text">Relatório de gerenciamento em tempo real;</p>
            </div>
            <div class="vantagem-item animate-slide-left-delay-5">
              <span class="vantagem-checkbox" aria-hidden="true"></span>
              <p class="vantagem-text">Recarga simplificada;</p>
            </div>
            <div class="vantagem-item animate-slide-left-delay-6">
              <span class="vantagem-checkbox" aria-hidden="true"></span>
              <p class="vantagem-text">Controle de estoque.</p>
            </div>
          </div>
        </div>
      </section>

      <!-- Título da Seção Clientes -->
      <section id="clientes" class="clientes-title-section">
        <div class="clientes-title-container animate-fade-in">
          <img src="/landing/images/clients_aiconsumer.png" alt="Clientes AiConsumer" class="clientes-title-image animate-scale-in">
        </div>
      </section>

      <!-- Seção Logos dos Clientes -->
      <section class="clientes-logos-section">
        <div class="clientes-logos-container animate-fade-in">
          <div class="clientes-logos-grid">
            <div class="cliente-logo animate-bounce-in">
              <img src="/landing/logos/elohim.png" alt="Elohim Colégio e Curso" class="cliente-logo-image">
            </div>
            <div class="cliente-logo animate-bounce-in-delay-1">
              <img src="/landing/logos/ina.png" alt="Colégio INA" class="cliente-logo-image">
            </div>
            <div class="cliente-logo animate-bounce-in-delay-2">
              <img src="/landing/logos/adventista.png" alt="Colégio Adventista" class="cliente-logo-image">
            </div>
            <div class="cliente-logo animate-bounce-in-delay-3">
              <img src="/landing/logos/logo selm.png" alt="SELM" class="cliente-logo-image">
            </div>
            <div class="cliente-logo animate-bounce-in-delay-4">
              <img src="/landing/logos/batista.png" alt="Cantina Batista" class="cliente-logo-image">
            </div>
            <div class="cliente-logo animate-bounce-in-delay-5">
              <img src="/landing/logos/evo.png" alt="EVO Cristo Group" class="cliente-logo-image">
            </div>
            <div class="cliente-logo animate-bounce-in-delay-6">
              <img src="/landing/logos/cath.png" alt="Cath Cantina" class="cliente-logo-image">
            </div>
            <div class="cliente-logo animate-bounce-in-delay-7">
              <img src="/landing/logos/fast_eat.png" alt="Fast Eat" class="cliente-logo-image">
            </div>
          </div>
        </div>
      </section>

      <!-- Botão Fale Conosco -->
      <section class="fale-conosco-section">
        <div class="fale-conosco-container animate-fade-in">
          <button class="fale-conosco-button" @click="openWhatsApp">
            <svg class="whatsapp-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" fill="currentColor"/>
            </svg>
            FALE CONOSCO
          </button>
        </div>
      </section>

    </main>

    <!-- Footer -->
    <footer class="footer">
      <div class="footer-container">
        <!-- Logo -->
        <div class="footer-logo">
          <a
            href="https://intelite.com.br/"
            target="_blank"
            rel="noopener noreferrer"
            class="footer-logo-link"
            aria-label="Acessar site da Intelite"
          >
            <img
              src="/landing/logos/logo-intelite.png"
              alt="Intelite Logo"
              class="footer-logo-image"
            />
          </a>
        </div>

        <!-- Informações de Contato -->
        <div class="footer-info">
          <!-- Endereço -->
          <div class="footer-column">
            <h3 class="footer-title">ENDEREÇO</h3>
            <div class="footer-item">
              <svg class="footer-icon" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
              </svg>
              <p class="footer-text">
                R. Carteiro Olivio Pontes, 575 - Água Fria<br />
                João Pessoa - PB, 58053-020
              </p>
            </div>
          </div>

          <!-- Contato -->
          <div class="footer-column">
            <h3 class="footer-title">CONTATO</h3>
            <div class="footer-contacts">
              <a
                href="https://wa.me/558335125222?text=Olá! Gostaria de saber mais sobre os serviços da Intelite Tecnologia."
                target="_blank"
                rel="noopener noreferrer"
                class="footer-link"
              >
                <svg class="footer-icon" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                </svg>
                (83) 3512-5222
              </a>
              <a 
                href="mailto:comercial@intelite.com.br" 
                class="footer-link"
              >
                <svg class="footer-icon" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                </svg>
                comercial@intelite.com.br
              </a>
            </div>
          </div>

          <!-- Redes Sociais -->
          <div class="footer-column">
            <h3 class="footer-title">REDES SOCIAIS</h3>
            <div class="footer-social">
              <a
                href="https://www.instagram.com/intelite_tecnologia/"
                target="_blank"
                rel="noopener noreferrer"
                class="footer-social-link"
                aria-label="Instagram da Intelite"
              >
                <svg class="footer-icon" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </a>
              <a
                href="https://www.linkedin.com/company/intelite-tecnologia/"
                target="_blank"
                rel="noopener noreferrer"
                class="footer-social-link"
                aria-label="LinkedIn da Intelite"
              >
                <svg class="footer-icon" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
            </div>
          </div>
        </div>

        <!-- Copyright -->
        <div class="footer-copyright">
          <p>&copy; 2026 Intelite Tecnologia. Todos os direitos reservados.</p>
        </div>
      </div>
    </footer>

    <!-- Botão flutuante WhatsApp -->
    <a
      class="whatsapp-floating"
      href="https://wa.me/558335125222?text=Ol%C3%A1!%20Gostaria%20de%20saber%20mais%20sobre%20os%20servi%C3%A7os%20da%20Intelite%20Tecnologia."
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Abrir conversa no WhatsApp"
    >
      <svg class="whatsapp-floating-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" fill="currentColor"/>
      </svg>
    </a>
  </div>
</template>

<script>
export default {
  name: 'Site',
  data() {
    return {
      isMenuOpen: false,
      activeSection: 'home',
      isScrolling: false,
      menuItems: [
        { id: 'home', label: 'HOME' },
        { id: 'sobre', label: 'SOBRE' },
        { id: 'vantagens', label: 'VANTAGENS' },
        { id: 'clientes', label: 'CLIENTES' },
        { id: 'contato', label: 'CONTATO' }
      ],
      vantagensGridTextos: [
        'Sem investimento em equipamentos',
        'Basta um smartphone',
        'Recarga feita direto no perfil dos alunos',
        'Compras registradas por reconhecimento facial',
        'Suporte imediato e recargas por meio de IA'
      ]
    }
  },
  mounted() {
    window.addEventListener('scroll', this.updateActiveSection)
    this.updateActiveSection()
    this.initScrollAnimations()
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.updateActiveSection)
    if (this.observer) {
      this.observer.disconnect()
    }
  },
  methods: {
    initScrollAnimations() {
      // Configuração do Intersection Observer
      const options = {
        threshold: 0.1, // Elemento deve estar 10% visível
        rootMargin: '0px 0px -50px 0px' // Margem para ativar antes de entrar completamente
      }

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-visible')
          }
        })
      }, options)

      // Observar todos os elementos com classes de animação
      this.$nextTick(() => {
        const animatedElements = document.querySelectorAll('[class*="animate-"]')
        animatedElements.forEach(el => {
          this.observer.observe(el)
        })
      })
    },

    toggleMenu() {
      this.isMenuOpen = !this.isMenuOpen
    },

    goToLogin() {
      this.$router.push('/login')
    },

    openWhatsApp() {
      const phoneNumber = '558335125222'
      const message = 'Olá! Gostaria de saber mais sobre os serviços da Intelite Tecnologia.'
      const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`
      window.open(whatsappUrl, '_blank')
    },
    
    scrollToSection(sectionId) {
      // Define imediatamente a seção ativa para evitar piscadas
      this.activeSection = sectionId
      this.isMenuOpen = false
      this.isScrolling = true // Desabilita updateActiveSection durante o scroll
      
      if (sectionId === 'contato') {
        // Para contato, vai para o footer
        const footer = document.querySelector('.footer')
        if (footer) {
          const headerHeight = 80
          const targetPosition = footer.offsetTop - headerHeight
          
          // Scroll suave com animação personalizada
          this.smoothScrollTo(targetPosition)
        }
      } else {
        // Para outras seções, comportamento normal
        const element = document.getElementById(sectionId)
        if (element) {
          const headerHeight = 80
          const targetPosition = element.offsetTop - headerHeight
          
          // Scroll suave com animação personalizada
          this.smoothScrollTo(targetPosition)
        }
      }
    },

    smoothScrollTo(targetPosition) {
      const startPosition = window.pageYOffset
      const distance = targetPosition - startPosition
      const duration = 800 // 800ms
      let start = null

      const animation = (currentTime) => {
        if (start === null) start = currentTime
        const timeElapsed = currentTime - start
        const run = this.easeInOutQuad(timeElapsed, startPosition, distance, duration)
        window.scrollTo(0, run)
        if (timeElapsed < duration) {
          requestAnimationFrame(animation)
        } else {
          // Reabilita updateActiveSection quando terminar o scroll
          this.isScrolling = false
        }
      }

      requestAnimationFrame(animation)
    },

    easeInOutQuad(t, b, c, d) {
      t /= d / 2
      if (t < 1) return c / 2 * t * t + b
      t--
      return -c / 2 * (t * (t - 2) - 1) + b
    },

    updateActiveSection() {
      // Não atualiza durante o scroll programático
      if (this.isScrolling) return
      
      const sections = ['home', 'sobre', 'vantagens', 'clientes']
      const headerHeight = 100

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId)
        if (element) {
          const rect = element.getBoundingClientRect()
          
          if (rect.top <= headerHeight && rect.bottom > headerHeight) {
            // Só atualiza se for diferente da seção atual
            if (this.activeSection !== sectionId) {
              this.activeSection = sectionId
            }
            return
          }
        }
      }
      
      // Se chegou até aqui, verifica se está no footer
      const footer = document.querySelector('.footer')
      if (footer) {
        const rect = footer.getBoundingClientRect()
        // Só marca como contato se estiver realmente no footer
        if (rect.top <= headerHeight && rect.bottom > headerHeight) {
          if (this.activeSection !== 'contato') {
            this.activeSection = 'contato'
          }
        }
      }
    }
  }
}
</script>

<style scoped>
/* Reset e Base */
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html {
  scroll-behavior: smooth;
  overflow-x: hidden;
}

body {
  overflow-x: hidden;
}

.landing-page {
  font-family: 'Arial', sans-serif;
  line-height: 1.6;
  overflow-x: hidden;
  width: 100%;
  max-width: 100vw;
}

/* Header */
.header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  width: 100%;
  background: white;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  z-index: 50;
  height: 86px; 
  border-top: 3px solid #0f0f41;
  transition: all 0.3s ease;
}

@media (max-width: 768px) {
  .header {
    height: 66px;
  }
}

.header-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

@media (max-width: 768px) {
  .header-container {
    padding: 15px 16px;
    min-height: 70px;
    display: flex;
    align-items: center;
  }
}

.nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  position: relative;
}

.logo {
  width: 160px;
  overflow: visible; /* evita cortes da imagem ao ajustar a posição */
}

.logo a {
  text-decoration: none;
  display: block;
}

.logo-image {
  height: auto;
  width: 100%;
  margin-left: 0; /* evita sumir com zoom e mantém dentro do container */
  transition: opacity 0.3s ease;
}

/* Aproxima somente a imagem no desktop, mantendo layout intacto */
@media (min-width: 960px) {
  .logo-image {
    margin-left: 0;
    transform: translateX(-30px); /* deslocamento moderado no 100% */
  }
}

@media (min-width: 1440px) {
  .logo-image {
    transform: translateX(-60px); /* telas grandes */
  }
}

@media (min-width: 1920px) {
  .logo-image {
    transform: translateX(-90px); /* ultrawide */
  }
}

@media (max-width: 768px) {
  .logo-image {
    margin-left: 0px;
  }
}

.logo a:hover .logo-image {
  opacity: 0.8;
}

.nav-desktop {
  display: flex;
  flex: 1;
  justify-content: center;
  gap: 48px;
}

.login-desktop {
  display: flex;
  align-items: center;
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
}

.nav-button {
  color: #374151;
  background: none;
  border: none;
  font-weight: 700;
  position: relative;
  padding: 8px 0;
  cursor: pointer;
  transition: color 0.3s ease;
  text-transform: uppercase;
  font-size: 16px;
}

.nav-button:hover {
  color: #111827;
}

.nav-button.active {
  color: #2B3674;
  font-weight: 700;
}

.nav-underline {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background-color: #2B3674;
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 0.3s ease;
}

.nav-underline.active {
  transform: scaleX(1);
}

.login-button {
  background: #2B3674;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 10px 20px;
  font-weight: 700;
  font-size: 14px;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
}

.login-button:hover {
  background: #1e2a5e;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(43, 54, 116, 0.3);
}

.login-icon {
  width: 16px;
  height: 16px;
}

.menu-toggle {
  display: none;
  background: none;
  border: none;
  color: #374151;
  cursor: pointer;
  position: absolute;
  right: 60px;
  top: 50%;
  transform: translateY(-50%);
}

.menu-toggle:hover {
  color: #111827;
}

.menu-icon {
  width: 24px;
  height: 24px;
}

@media (max-width: 768px) {
  .menu-icon {
    width: 32px;
    height: 32px;
  }
}

.overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 30;
}

.nav-mobile {
  position: fixed;
  top: 60px;
  left: 0;
  right: 0;
  background: white;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  z-index: 40;
  transform: translateY(-100%);
  opacity: 0;
  pointer-events: none;
  transition: all 0.3s ease-in-out;
}

.nav-mobile.open {
  transform: translateY(0);
  opacity: 1;
  pointer-events: auto;
}

.nav-mobile-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  padding: 40px 0;
}

.nav-button-mobile {
  color: #374151;
  background: none;
  border: none;
  font-weight: 700;
  font-size: 20px;
  cursor: pointer;
  transition: color 0.3s ease;
}

.nav-button-mobile:hover {
  color: #111827;
}

.nav-button-mobile.active {
  color: #2B3674;
  font-weight: 700;
}

.login-button-mobile {
  background: #2B3674;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 12px 24px;
  font-weight: 700;
  font-size: 16px;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 16px;
}

.login-button-mobile:hover {
  background: #1e2a5e;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(43, 54, 116, 0.3);
}

/* Main Content */
.main {
  margin-top: 86px;
}

@media (max-width: 768px) {
  .main {
    margin-top: 66px;
  }
}

.section {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  margin: 0;
  border-radius: 8px;
}

.home-section {
  margin-top: 0 !important;
  margin-bottom: 0 !important;
  border-radius: 0 !important;
  width: 100%;
}

.home-background-container {
  position: relative;
  width: 100%;
  height: clamp(320px, 45vh, 450px);
  min-height: 320px;
  overflow: hidden;
}

.home-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  z-index: 1;
}

/* Home Content */
.home-content {
  position: relative;
  z-index: 3;
  display: flex !important;
  align-items: flex-end !important;
  justify-content: space-between !important;
  width: 100% !important;
  height: 100% !important;
  padding: 0 clamp(16px, 5%, 60px) !important;
  margin: 0 !important;
  max-width: 1200px;
  margin-left: auto !important;
  margin-right: auto !important;
  box-sizing: border-box !important;
}

.home-left {
  flex: 1;
  min-width: 0;
  display: flex !important;
  justify-content: flex-start !important;
  align-items: flex-end !important;
  height: 100% !important;
}

.home-center {
  flex: 1;
  min-width: 0;
  display: flex !important;
  justify-content: flex-start !important;
  align-items: center !important;
  height: 100% !important;
  text-align: left !important;
  padding-left: 0 !important;
  margin-left: clamp(-80px, -6vw, -40px) !important;
  padding-top: 0 !important;
  padding-right: min(2.5vw, 28px) !important;
  box-sizing: border-box !important;
}

.home-right {
  flex: 1;
  min-width: 0;
  display: flex !important;
  justify-content: flex-end !important;
  align-items: flex-end !important;
  height: 100% !important;
  margin-right: clamp(-100px, -8vw, -40px) !important;
}

.home-person {
  width: auto !important;
  max-width: min(100%, 600px) !important;
  max-height: min(2000px, 95%) !important;
  height: auto !important;
  object-fit: contain !important;
  object-position: bottom left !important;
  transform: translate(clamp(-80px, -6vw, -40px), clamp(120px, 18vw, 200px)) scale(clamp(1.1, 1.2vw, 1.45)) !important;
}

.home-cards {
  position: absolute !important;
  bottom: 0 !important;
  right: 0 !important;
  width: min(780px, 38vw) !important;
  max-width: 780px !important;
  height: auto !important;
  object-fit: contain !important;
  object-position: bottom right !important;
  display: block !important;
  z-index: 2 !important;
  pointer-events: none !important;
  transform: translateX(min(40px, 2.5vw)) !important;
}

.home-title {
  color: #0f0f41 !important;
  font-size: clamp(1.5rem, 2.5vw + 1rem, 3.2rem) !important;
  font-weight: 700 !important;
  line-height: 1.1 !important;
  text-align: left !important;
  margin: 0 !important;
  padding: 0 !important;
  width: auto !important;
  display: block !important;
}

.text-white {
  color: white !important;
}

.text-line {
  display: block;
  opacity: 0;
  animation: fadeInUp 0.8s ease-out forwards;
}

.text-line:nth-child(1) { animation-delay: 0.2s; }
.text-line:nth-child(2) { animation-delay: 0.4s; }
.text-line:nth-child(3) { animation-delay: 0.6s; }
.text-line:nth-child(4) { animation-delay: 0.8s; }
.text-line:nth-child(5) { animation-delay: 1s; }

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Banner responsivo - 1200px */
@media (max-width: 1200px) {
  .home-background-container {
    height: clamp(300px, 42vh, 420px);
  }
  .home-content {
    padding: 0 4% !important;
  }
  .home-person {
    max-width: min(100%, 480px) !important;
    transform: translate(clamp(-60px, -5vw, -40px), clamp(100px, 15vw, 180px)) scale(1.35) !important;
  }
  .home-cards {
    width: min(620px, 36vw) !important;
    transform: translateX(min(24px, 1.5vw)) !important;
  }
  .home-title {
    font-size: clamp(1.4rem, 2.2vw + 0.8rem, 2.8rem) !important;
  }
}

/* Banner responsivo - 1024px */
@media (max-width: 1024px) {
  .home-background-container {
    height: clamp(280px, 40vh, 400px);
  }
  .home-center {
    margin-left: clamp(-50px, -5vw, -30px) !important;
    padding-right: min(2vw, 20px) !important;
  }
  .home-person {
    max-width: min(100%, 420px) !important;
    transform: translate(clamp(-50px, -4vw, -30px), clamp(80px, 12vw, 160px)) scale(1.25) !important;
  }
  .home-cards {
    width: min(520px, 34vw) !important;
    transform: translateX(min(16px, 1vw)) !important;
  }
  .home-title {
    font-size: clamp(1.3rem, 2vw + 0.6rem, 2.5rem) !important;
  }
}

/* Banner responsivo - 768px (tablet/mobile: só texto + bolsa centralizada) */
@media (max-width: 768px) {
  .home-background-container {
    height: clamp(260px, 38vh, 400px);
    min-height: 260px;
  }
  .home-content {
    justify-content: center !important;
    align-items: center !important;
    padding: 16px 6% !important;
  }
  .home-left {
    display: none !important;
  }
  .home-right {
    flex: none !important;
    margin: 0 !important;
  }
  .home-center {
    flex: none !important;
    margin-left: 0 !important;
    padding-right: 0 !important;
    padding-top: 36px !important;
    text-align: center !important;
    justify-content: center !important;
    align-items: center !important;
    margin-top: 0 !important;
  }
  .home-cards {
    left: auto !important;
    right: -38% !important;
    width: min(300px, 85vw) !important;
    max-width: 300px !important;
    transform: translateX(0) !important;
    bottom: 0 !important;
  }
  .home-title {
    font-size: clamp(1.75rem, 6vw, 3.1rem) !important;
    text-align: center !important;
  }
}

/* Banner responsivo - 480px */
@media (max-width: 480px) {
  .home-background-container {
    height: clamp(240px, 35vh, 350px);
    min-height: 240px;
  }
  .home-content {
    padding: 12px 5% !important;
    margin-top: 0 !important;
  }
  .home-center {
    margin-top: 0 !important;
    padding-top: 32px !important;
  }
  .home-cards {
    left: auto !important;
    right: -35% !important;
    width: min(250px, 80vw) !important;
    max-width: 250px !important;
    transform: none !important;
    bottom: 0 !important;
  }
  .home-title {
    font-size: clamp(1.65rem, 5.8vw, 2.85rem) !important;
  }
}

/* Banner responsivo - 360px (telas muito pequenas) */
@media (max-width: 360px) {
  .home-background-container {
    height: clamp(220px, 32vh, 320px);
    min-height: 220px;
  }
  .home-content {
    padding: 10px 4% !important;
  }
  .home-center {
    padding-top: 28px !important;
  }
  .home-cards {
    left: auto !important;
    right: -30% !important;
    width: min(220px, 78vw) !important;
    max-width: 220px !important;
    transform: none !important;
    bottom: 0 !important;
  }
  .home-title {
    font-size: clamp(1.5rem, 5vw, 2.6rem) !important;
  }
}

/* Banner 2 */
.banner2-section {
  width: 100%;
  background: #fff;
  padding: 70px 0;
  margin: 0;
  border: none;
  outline: none;
}

@media (max-width: 768px) {
  .banner2-section {
    padding: 20px 0;
  }
  .banner2-content {
    padding: 0 8%;
  }
}

.banner2-content {
  max-width: 1400px;
  margin: 0 auto;
  width: 100%;
  padding: 0 20px;
}

.banner2-image {
  width: 100%;
  height: auto;
  display: block;
  object-fit: cover;
  min-height: 420px;
}

/* Mobile: mostrar mais conteúdo reduzindo a altura e o padding */
@media (max-width: 600px) {
  .banner2-section {
    padding: 15px 0;
  }
  .banner2-content {
    padding: 0 6%;
  }
  .banner2-image {
    min-height: 300px;       /* banner mais baixo no mobile */
    object-position: center top; /* prioriza a parte superior da imagem */
  }
}

@media (max-width: 480px) {
  .banner2-content {
    padding: 0 6%;
  }
}

/* Seção Sobre */
.sobre-section {
  background: #fff;
  margin: 0;
  border-radius: 0;
}

.sobre-section .section-content {
  max-width: 1400px;
  margin: 0 auto;
  padding: 10px 0;
  text-align: left;
}

.sobre-section h1 {
  color: #0f0f41;
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 32px;
  text-align: left !important;
  margin-left: 0 !important;
  padding: 0 20px !important;
  margin-top: 0 !important;
}

@media (min-width: 768px) {
  .sobre-section h1 {
    font-size: 30px;
  }
}

@media (min-width: 1024px) {
  .sobre-section h1 {
    font-size: 36px;
  }
}

.sobre-section p {
  color: #0f0f41;
  font-size: 18px;
  font-weight: 400;
  line-height: 1.75;
  margin: 0 !important;
  text-align: justify;
  margin-left: 0 !important;
  padding: 0 20px !important;
}

@media (min-width: 768px) {
  .sobre-section p {
    font-size: 20px;
  }
}

/* Refinos mobile para título "Sobre" */
@media (max-width: 480px) {
  .sobre-section h1 {
    font-size: 20px;
    line-height: 1.35;
    margin-bottom: 16px;
    letter-spacing: 0;
    word-break: keep-all;
  }
  .sobre-section .section-content {
    padding: 40px 0;
  }
}

@media (min-width: 1024px) {
  .sobre-section p {
    font-size: 20px;
  }
}

/* Seção Título Vantagens */
.vantagens-title-section {
  background: #fff;
  padding: 40px 0;
  margin: 0;
}

.vantagens-title-container {
  max-width: 896px;
  width: 100%;
  margin: 0 auto;
  padding: 0 16px;
}

.vantagens-title-image {
  width: 100%;
  height: auto;
  display: block;
  margin: 0 auto;
}

/* Seção Vantagens */
.vantagens-section {
  background: #fff;
  padding: 40px 0;
  margin: 0;
}

.vantagens-container {
  max-width: 1400px;
  width: 100%;
  margin: 0 auto;
  padding: 0 20px;
}

.vantagens-list {
  display: flex;
  flex-direction: column;
  gap: 2px; /* Reduzido de 4px para 2px */
}

.vantagem-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 4px 0; /* py-1 = 4px (top + bottom) */
  color: #0f0f41; /* garante que o checkbox herde a mesma cor do texto */
}

.vantagem-checkbox {
  display: inline-block;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  background-color: currentColor; /* mesma cor do texto */
  -webkit-mask-image: url('/landing/images/Check.png');
  mask-image: url('/landing/images/Check.png');
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
  -webkit-mask-position: center;
  mask-position: center;
  -webkit-mask-size: contain;
  mask-size: contain;
}

.vantagem-text {
  font-size: 18px;
  line-height: 29px;
  color: currentColor;
  margin: 0;
  font-weight: 700;
}

/* Desktop */
@media (min-width: 768px) {
  .vantagem-checkbox {
    width: 28px;
    height: 28px;
  }
  
  .vantagem-text {
    font-size: 20px;
    line-height: 32.5px;
  }
}

/* Grid de imagens de vantagens */
.vantagens-grid-section {
  background: #fff;
  padding: 40px 0 60px;
  margin: 0;
}

.vantagens-grid-container {
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  padding: 0 20px;
}

.vantagens-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: clamp(18px, 2vw, 32px);
  align-items: start;
  justify-items: center;
}

.vantagens-grid-item {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  max-width: 220px;
  min-height: 0;
  text-align: center;
}

.vantagens-grid-check {
  width: 52px;
  height: 52px;
  object-fit: contain;
  flex-shrink: 0;
  margin-bottom: 12px;
}

.vantagens-grid-texto {
  color: #ff5a00;
  font-size: 20px;
  font-weight: 800;
  line-height: 1.3;
  margin: 0;
}

@media (max-width: 1024px) {
  .vantagens-grid-container {
    max-width: 800px;
  }
  .vantagens-grid {
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;
  }
  .vantagens-grid-item {
    max-width: 200px;
  }
  .vantagens-grid-check {
    width: 44px;
    height: 44px;
    margin-bottom: 10px;
  }
  .vantagens-grid-texto {
    font-size: 18px;
    font-weight: 800;
  }
}

@media (max-width: 768px) {
  .vantagens-grid-section {
    padding: 28px 0 40px;
  }
  .vantagens-grid-container {
    padding: 0 16px;
    max-width: 560px;
  }
  .vantagens-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  }
  .vantagens-grid-item {
    max-width: 200px;
  }
  .vantagens-grid-check {
    width: 44px;
    height: 44px;
    margin-bottom: 10px;
  }
  .vantagens-grid-texto {
    font-size: 18px;
    font-weight: 800;
  }
}

@media (max-width: 480px) {
  .vantagens-grid-section {
    padding: 24px 0 32px;
  }
  .vantagens-grid-container {
    padding: 0 12px;
  }
  .vantagens-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
    max-width: none;
    margin: 0;
  }
  .vantagens-grid-item {
    max-width: none;
    margin: 0;
  }
  .vantagens-grid-item:last-child {
    grid-column: 1 / -1;
    justify-self: center;
    max-width: 180px;
  }
  .vantagens-grid-check {
    width: 44px;
    height: 44px;
    margin-bottom: 8px;
  }
  .vantagens-grid-texto {
    font-size: 14px;
    font-weight: 800;
  }
}

/* Seção Título Clientes */
.clientes-title-section {
  background: #fff;
  padding: 40px 0;
  margin: 0;
}

.clientes-title-container {
  max-width: 896px;
  width: 100%;
  margin: 0 auto;
  padding: 0 16px;
}

.clientes-title-image {
  width: 100%;
  height: auto;
  display: block;
  margin: 0 auto;
}

/* Seção Logos dos Clientes */
.clientes-logos-section {
  background: #fff;
  padding: 40px 0;
  margin: 0;
}

.clientes-logos-container {
  max-width: 1400px;
  width: 100%;
  margin: 0 auto;
  padding: 0 20px;
}

.clientes-logos-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 15px;
  align-items: center;
  justify-items: center;
}

.cliente-logo {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 120px;
}

.cliente-logo-image {
  max-width: 100%;
  max-height: 100%;
  height: auto;
  width: auto;
  object-fit: contain;
}

/* Responsivo */
@media (max-width: 768px) {
  .clientes-logos-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }
  
  .cliente-logo {
    height: 80px;
  }
}

/* Seção Fale Conosco */
.fale-conosco-section {
  background: #fff;
  padding: 40px 0 80px;
  margin: 0;
  overflow-x: hidden;
  position: relative;
  border: none !important;
  border-top: none !important;
  border-bottom: none !important;
}

.fale-conosco-container {
  max-width: 1400px;
  width: 100%;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
}

.fale-conosco-button {
  background: #0f0f41;
  color: white;
  border: none;
  border-radius: 27px;
  padding: 16px 32px;
  font-size: 18px;
  font-weight: 700;
  text-transform: uppercase;
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  z-index: 1;
}

.fale-conosco-button:hover {
  background: #1a1a5a;
  transform: scale(1.05);
}

.whatsapp-icon {
  width: 24px;
  height: 24px;
  fill: currentColor;
}


.section-content h1 {
  color: #2B3674;
  font-size: 2.5rem;
  font-weight: 700;
  text-align: center;
}

.home-section .section-content h1 {
  color: white;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
}

/* Footer */
.footer {
  background-color: #1a1f47;
  color: white;
  padding: 48px 0;
}

.footer-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  text-align: center;
}

.footer-logo {
  margin-bottom: 48px;
}

.footer-logo-link {
  display: inline-block;
  text-decoration: none;
  transition: transform 0.3s ease;
}

.footer-logo-link:hover {
  transform: scale(1.05);
}

.footer-logo-image {
  height: 48px;
  filter: brightness(0) invert(1);
}

.footer-info {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 40px;
  margin-bottom: 48px;
}

.footer-column {
  text-align: center;
}

.footer-title {
  font-weight: 600;
  font-size: 18px;
  margin-bottom: 16px;
  color: white;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.footer-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  justify-content: center;
}

.footer-icon {
  color: white;
  margin-top: 2px;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

.footer-text {
  color: white;
  margin: 0;
  line-height: 1.5;
  text-align: left;
}

.footer-contacts {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
}

.footer-link {
  display: flex;
  align-items: center;
  gap: 8px;
  color: white;
  text-decoration: none;
  transition: color 0.3s ease;
}

.footer-link:hover {
  color: #d1d5db;
}

.footer-social {
  display: flex;
  gap: 16px;
  justify-content: center;
}

.footer-social-link {
  color: white;
  text-decoration: none;
  transition: color 0.3s ease;
}

.footer-social-link:hover {
  color: #d1d5db;
}

.footer-copyright {
  margin-top: 48px;
  padding-top: 32px;
  border-top: 1px solid #4b5563;
  text-align: center;
}

.footer-copyright p {
  margin: 0;
  color: white;
}

/* Botão flutuante WhatsApp */
.whatsapp-floating {
  position: fixed;
  right: 20px;
  bottom: 20px;
  width: 56px;
  height: 56px;
  border-radius: 999px;
  background: #25d366;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  text-decoration: none;
}

.whatsapp-floating:hover {
  transform: scale(1.08);
  box-shadow: 0 10px 28px rgba(37, 211, 102, 0.45);
}

.whatsapp-floating-icon {
  width: 28px;
  height: 28px;
  display: block;
}

/* Responsive */
@media (max-width: 768px) {
  .nav-desktop {
    display: none;
  }
  
  .login-desktop {
    display: none;
  }
  
  .menu-toggle {
    display: block;
    right: 15px;
  }
  
  .header-container {
    padding: 0 15px;
  }
  
  .logo {
    width: 140px;
  }
  
  .section-content h1 {
    font-size: 2rem;
  }
  
  .footer-info {
    grid-template-columns: 1fr;
    gap: 30px;
  }
  
  .footer-text {
    text-align: center;
  }
  
  .footer-social-link {
    font-size: 0;
  }
  
  .footer-social-link .footer-icon {
    font-size: 16px;
  }
}

@media (min-width: 960px) {
  .footer-column {
    text-align: left;
  }
  
  .footer-item {
    justify-content: flex-start;
  }
  
  .footer-contacts {
    align-items: flex-start;
  }
  
  .footer-social {
    justify-content: flex-start;
  }
}

/* Animações */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideLeft {
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes scaleIn {
  from {
    opacity: 0;
    transform: scale(0.8);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes bounceIn {
  0% {
    opacity: 0;
    transform: scale(0.3);
  }
  50% {
    opacity: 1;
    transform: scale(1.05);
  }
  70% {
    transform: scale(0.9);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
}

/* Classes de animação - Estado inicial (invisível) */
.animate-fade-in,
.animate-slide-up,
.animate-slide-up-delay,
.animate-slide-left,
.animate-slide-left-delay-1,
.animate-slide-left-delay-2,
.animate-slide-left-delay-3,
.animate-slide-left-delay-4,
.animate-slide-left-delay-5,
.animate-slide-left-delay-6,
.animate-scale-in,
.animate-bounce-in,
.animate-bounce-in-delay-1,
.animate-bounce-in-delay-2,
.animate-bounce-in-delay-3,
.animate-bounce-in-delay-4,
.animate-bounce-in-delay-5,
.animate-bounce-in-delay-6,
.animate-bounce-in-delay-7 {
  opacity: 0;
  transform: translateY(30px);
}

/* Animações só executam quando visível */
.animate-visible.animate-fade-in {
  animation: fadeIn 0.8s ease-out forwards;
}

.animate-visible.animate-slide-up {
  animation: slideUp 0.8s ease-out forwards;
}

.animate-visible.animate-slide-up-delay {
  animation: slideUp 0.8s ease-out 0.2s forwards;
}

.animate-visible.animate-slide-left {
  animation: slideLeft 0.6s ease-out forwards;
}

.animate-visible.animate-slide-left-delay-1 {
  animation: slideLeft 0.6s ease-out 0.1s forwards;
}

.animate-visible.animate-slide-left-delay-2 {
  animation: slideLeft 0.6s ease-out 0.2s forwards;
}

.animate-visible.animate-slide-left-delay-3 {
  animation: slideLeft 0.6s ease-out 0.3s forwards;
}

.animate-visible.animate-slide-left-delay-4 {
  animation: slideLeft 0.6s ease-out 0.4s forwards;
}

.animate-visible.animate-slide-left-delay-5 {
  animation: slideLeft 0.6s ease-out 0.5s forwards;
}

.animate-visible.animate-slide-left-delay-6 {
  animation: slideLeft 0.6s ease-out 0.6s forwards;
}

.animate-visible.animate-scale-in {
  animation: scaleIn 0.8s ease-out forwards;
}

.animate-visible.animate-bounce-in {
  animation: bounceIn 0.8s ease-out forwards;
}

.animate-visible.animate-bounce-in-delay-1 {
  animation: bounceIn 0.8s ease-out 0.1s forwards;
}

.animate-visible.animate-bounce-in-delay-2 {
  animation: bounceIn 0.8s ease-out 0.2s forwards;
}

.animate-visible.animate-bounce-in-delay-3 {
  animation: bounceIn 0.8s ease-out 0.3s forwards;
}

.animate-visible.animate-bounce-in-delay-4 {
  animation: bounceIn 0.8s ease-out 0.4s forwards;
}

.animate-visible.animate-bounce-in-delay-5 {
  animation: bounceIn 0.8s ease-out 0.5s forwards;
}

.animate-visible.animate-bounce-in-delay-6 {
  animation: bounceIn 0.8s ease-out 0.6s forwards;
}

.animate-visible.animate-bounce-in-delay-7 {
  animation: bounceIn 0.8s ease-out 0.7s forwards;
}

</style>

